import type { ReactNode } from "react"
import { Loader2 } from "lucide-react"
import { STATUS_LABELS, isOverdue, stageLabel, type Complaint } from "../../shared/domain"
import { useApp } from "@/lib/app-context"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

export function Loading({ label = "A carregar…" }: { label?: string }) {
  return (
    <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {label}
    </div>
  )
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
      {message}
      {onRetry ? (
        <button className="ml-2 underline" onClick={onRetry}>
          Tentar de novo
        </button>
      ) : null}
    </div>
  )
}

export function Panel({ title, action, children, className }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("rounded-xl border border-border/60 bg-card shadow-sm", className)}>
      {title || action ? (
        <header className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          {action}
        </header>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{children}</p>
}

export function StatusBadge({ c }: { c: Pick<Complaint, "status" | "due_at" | "stage"> }) {
  const { settings } = useApp()
  if (c.status === "cancelled") return <Badge variant="neutral">{STATUS_LABELS.cancelled}</Badge>
  if (c.status === "closed")
    return <Badge variant="success">{c.stage === "eficacia" ? stageLabel(settings.stages, "eficacia") : STATUS_LABELS.closed}</Badge>
  if (isOverdue(c)) return <Badge variant="destructive">Prazo ultrapassado</Badge>
  return <Badge variant="info">{stageLabel(settings.stages, c.stage)}</Badge>
}

export function SeverityBadge({ severity, label }: { severity: string | null; label?: string }) {
  if (!severity) return <span className="text-muted-foreground">—</span>
  const variant = severity === "alta" ? "destructive" : severity === "media" ? "warning" : "neutral"
  return <Badge variant={variant}>{label ?? severity}</Badge>
}

export function Avatar({ name, className }: { name: string | null | undefined; className?: string }) {
  const ini = (name ?? "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("")
  return (
    <span
      className={cn(
        "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 text-[10px] font-semibold text-accent",
        className,
      )}
      title={name ?? undefined}
    >
      {ini || "?"}
    </span>
  )
}

/** Barras horizontais simples (motivos, categorias). */
export function HBars({ items }: { items: Array<{ label: string; value: number }> }) {
  const max = Math.max(1, ...items.map((i) => i.value))
  return (
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={i.label} className="text-sm">
          <div className="mb-1 flex justify-between gap-2">
            <span className="truncate">{i.label}</span>
            <span className="tabular-nums text-muted-foreground">{i.value}</span>
          </div>
          <div className="h-2 rounded-full bg-secondary">
            <div className="h-2 rounded-full bg-accent" style={{ width: `${(i.value / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  )
}

/** Colunas verticais por mês. `value` null = sem dados (coluna vazia). */
export function MonthColumns({
  items,
  max,
  format,
}: {
  items: Array<{ label: string; value: number | null; hint?: string }>
  max?: number
  format?: (v: number) => string
}) {
  const top = max ?? Math.max(1, ...items.map((i) => i.value ?? 0))
  return (
    <div className="flex h-40 items-end gap-1.5">
      {items.map((i) => (
        <div key={i.label} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1" title={i.hint}>
          <span className="text-[10px] tabular-nums text-muted-foreground">{i.value == null ? "" : format ? format(i.value) : i.value}</span>
          <div
            className={cn("w-full rounded-t-md", i.value == null ? "bg-secondary" : "bg-accent/80")}
            style={{ height: `${i.value == null ? 4 : Math.max(4, (i.value / top) * 100)}%` }}
          />
          <span className="text-[10px] text-muted-foreground">{i.label}</span>
        </div>
      ))}
    </div>
  )
}
